import { ProductType } from "@/constants";

/**
 * Booking entity legal companies:
 * - UP: Universe Payments Limited (fiat currencies, FCA-regulated)
 * - UT: Universe Technologies (digital assets, being phased out in favour of UM)
 * - UM: Universe Money LLC (digital assets, US / FinCEN-registered MSB)
 */
export type CompanyType = "UP" | "UT" | "UM";

export interface CompanyConfig {
  name: string;
  logoPath: string;
  address: string[];
  footerText: string[];
}

const UniverseMoneyFooterText = [
  "Universe Money is a trading name of Endor Group Limited, registered in England & Wales. Company no. 14101534.",
  "Registered Office: Endor Group Limited, Fawley House, 2 Regatta Place, Bourne End, SL8 5TD. UK",
  "© 2026 Endor Group Limited. All rights reserved",
];

const UniverseMoneyLlcFooterText = [
  "Universe Money LLC is registered in the State of Montana, USA. Company No. C1639212.",
  "Universe Money LLC is a Money Service Business (MSB) registered with the Financial Crimes Enforcement Network (FINCEN) with registration number 31000334088300.",
];

/** Top-right brand lines on statement + settlement-instruction PDFs. */
const UniverseMoneyBrandAddress = [
  "https://www.universemoney.io",
  "support@universemoney.io",
];

export const companyConfigs: Record<CompanyType, CompanyConfig> = {
  UP: {
    name: "UNIVERSE MONEY",
    logoPath: "/images/um-logo-blue-cropped.png",
    address: UniverseMoneyBrandAddress,
    footerText: [
      ...UniverseMoneyFooterText,
      "Universe Payments Limited is registered in England & Wales No. 05021694",
      "Universe Payments Limited is authorised and regulated by the Financial Conduct Authority as an Authorised Payment Institution (firm reference number 554920)",
    ],
  },
  // UT (Universe Technologies) is being phased out in favour of UM (Universe
  // Money LLC) — during the transition, UT statements carry the LLC details.
  UT: {
    name: "UNIVERSE MONEY",
    logoPath: "/images/um-logo-blue-cropped.png",
    address: UniverseMoneyBrandAddress,
    footerText: [...UniverseMoneyFooterText, ...UniverseMoneyLlcFooterText],
  },
  UM: {
    name: "UNIVERSE MONEY",
    logoPath: "/images/um-logo-blue-cropped.png",
    address: UniverseMoneyBrandAddress,
    footerText: [...UniverseMoneyFooterText, ...UniverseMoneyLlcFooterText],
  },
};

/**
 * Resolves the legal company for a product type.
 *
 * Fiat (currencies) is always Universe Payments (UP). Digital assets can be
 * booked under Universe Technologies (UT) or Universe Money LLC (UM); UM is
 * taking over from UT, so it wins when both are present and is the fallback
 * when no codes are available. (During the phase-out UT's config carries the
 * LLC details anyway, so every digital-assets statement shows them.)
 */
export function getCompanyConfig(
  accountType: ProductType,
  bookingEntityCodes?: readonly string[]
): CompanyConfig {
  if (accountType === "currencies") {
    return companyConfigs.UP;
  }

  if (
    bookingEntityCodes?.includes("UT") &&
    !bookingEntityCodes.includes("UM")
  ) {
    return companyConfigs.UT;
  }
  return companyConfigs.UM;
}
